import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import {
    UserPlus,
    Building2,
    MapPin,
    Mail,
    Phone,
    Smartphone,
    Loader2,
} from "lucide-react";

const INPUT_CLS =
    "bg-[#0A1128] border-[#2A3655] text-white placeholder:text-slate-500 focus-visible:ring-[#007AFF]";

const BLANK = {
    name: "",
    company: "",
    address: "",
    city: "",
    country: "",
    email: "",
    work_phone: "",
    cell_phone: "",
    notes: "",
};

export default function NewContactDialog({ open, onOpenChange, onCreate }) {
    const [draft, setDraft] = useState(BLANK);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setDraft(BLANK);
    }, [open]);

    const update = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

    const canSubmit = draft.name.trim().length > 0 && !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            // Trim everything on the way out so the server never stores
            // stray whitespace.
            const payload = Object.fromEntries(
                Object.entries(draft).map(([k, v]) => [k, (v || "").trim()]),
            );
            await onCreate?.(payload);
            onOpenChange(false);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#131A2F] border border-[#2A3655] text-white w-[calc(100vw-1.5rem)] max-w-[560px] max-h-[90vh] p-4 sm:p-6 overflow-hidden"
                data-testid="new-contact-dialog"
            >
                <div className="flex flex-col gap-3 max-h-[calc(90vh-2rem)] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#00A3FF]" />
                            New contact
                        </DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Fill in what you have — only the name is required. You can edit
                            everything later.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Name <span className="text-red-400">*</span>
                            </Label>
                            <Input
                                autoFocus
                                data-testid="new-contact-name"
                                value={draft.name}
                                onChange={(e) => update("name", e.target.value)}
                                className={INPUT_CLS}
                                placeholder="Full name"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div className="space-y-1.5">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1">
                                    <Building2 className="w-3 h-3 text-[#00A3FF]" />
                                    Company
                                </Label>
                                <Input
                                    data-testid="new-contact-company"
                                    value={draft.company}
                                    onChange={(e) => update("company", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="Company name"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1">
                                    <Mail className="w-3 h-3 text-[#00A3FF]" />
                                    Email
                                </Label>
                                <Input
                                    data-testid="new-contact-email"
                                    type="email"
                                    value={draft.email}
                                    onChange={(e) => update("email", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="name@company.com"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1">
                                    <Phone className="w-3 h-3 text-[#00A3FF]" />
                                    Work phone
                                </Label>
                                <Input
                                    data-testid="new-contact-work-phone"
                                    type="tel"
                                    value={draft.work_phone}
                                    onChange={(e) => update("work_phone", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1">
                                    <Smartphone className="w-3 h-3 text-[#00A3FF]" />
                                    Cell phone
                                </Label>
                                <Input
                                    data-testid="new-contact-cell-phone"
                                    type="tel"
                                    value={draft.cell_phone}
                                    onChange={(e) => update("cell_phone", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="(555) 987-6543"
                                />
                            </div>
                            <div className="space-y-1.5 sm:col-span-2">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-[#00A3FF]" />
                                    Address
                                </Label>
                                <Input
                                    data-testid="new-contact-address"
                                    value={draft.address}
                                    onChange={(e) => update("address", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="Street address"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                    City
                                </Label>
                                <Input
                                    data-testid="new-contact-city"
                                    value={draft.city}
                                    onChange={(e) => update("city", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="City"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                    Country
                                </Label>
                                <Input
                                    data-testid="new-contact-country"
                                    value={draft.country}
                                    onChange={(e) => update("country", e.target.value)}
                                    className={INPUT_CLS}
                                    placeholder="Country"
                                />
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider">
                                Notes
                            </Label>
                            <textarea
                                data-testid="new-contact-notes"
                                rows={3}
                                value={draft.notes}
                                onChange={(e) => update("notes", e.target.value)}
                                placeholder="Project history, preferences, follow-up dates…"
                                className={`${INPUT_CLS} w-full border rounded-md px-3 py-2 resize-none`}
                            />
                        </div>
                        <DialogFooter className="gap-2 sm:gap-2 pt-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                data-testid="new-contact-cancel"
                                className="text-slate-300 hover:text-white hover:bg-[#1C253C]"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="new-contact-submit"
                                className="bg-[#007AFF] hover:bg-[#3395FF] text-white font-bold disabled:opacity-40 disabled:bg-[#2A3655] disabled:text-slate-400"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Saving…
                                    </>
                                ) : (
                                    <>
                                        <UserPlus className="w-4 h-4 mr-2" />
                                        Save contact
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
}
